import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ENDPOINT from '../../../constants/endpoints';
import AsyncSelectPane from '../../../GbsUI/components/AsyncSelectPane';
import Button from '../../../GbsUI/UI/Button';
import ArrowLeftIcon from '../../../GbsUI/UI/icons/ArrowLeft';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import InputLabel from '../../../GbsUI/UI/InputLabel';
import Radio from '../../../GbsUI/UI/Radio';
import SignupLayout from '../components/Layout';
import {
  DISCLOSE_OPTIONS,
  GENDER_OPTIONS,
  LGBTQ_OPTIONS,
  RACE_OPTIONS,
  US_AUTH_OPTIONS,
} from '../constants';
import { SignupContext } from '../context';
import stepSixSchema from '../schema/step-six';

const SignupStepSix = ({ history }) => {
  const { state, setState } = useContext(SignupContext);
  const [formErrors, setFormErrors] = useState(new Map());
  const initialized = useRef(false);

  const validate = useCallback(
    (data, abortEarly = false, scrollToError = true) => {
      return stepSixSchema
        .validate(data, { abortEarly })
        .then(data => {
          setFormErrors(new Map());
          return data;
        })
        .catch(errors => {
          if (errors.inner.length > 0) {
            const errorsMap = errors.inner.reduce((accum, item, index) => {
              const field = document.querySelector(`[name="${item.path}"]`);

              if (index === 0 && field && scrollToError) {
                field
                  .closest('.signup-form-item')
                  .scrollIntoView({ behavior: 'smooth' });
              }

              accum.set(item.path, item.errors);
              return accum;
            }, new Map());

            setFormErrors(errorsMap);
          } else {
            const errorsMap = new Map();
            errorsMap.set(errors.path, errors.errors);

            setFormErrors(errorsMap);
          }

          return false;
        });
    },
    []
  );

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      validate(state.workStatus).then(valid => {
        if (valid) {
          setFormErrors(new Map());
          history.push('/signup/step/7');
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      });
    },
    [history, state.workStatus, validate]
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    validate(state.workStatus, true, false);
  }, [state, validate]);

  return (
    <form onSubmit={onSubmit}>
      <SignupLayout
        title={(
          <span className={'flex justify-between gap-2'}>
            <span>
              Step 6: Work Status and Demographics
            </span>
            <span className={'text-small'}>
              Step 6 of 8
            </span>
          </span>
        )}
        description={'Please share your US work eligibility'}
        footer={
          <>
            <Button
              type={'button'}
              variant={'secondary'}
              size={'xl'}
              icon={ArrowLeftIcon}
              iconProps={{ size: 21 }}
              iconPosition={'left'}
              onClick={() => history.push('/signup/step/5')}
            >
              Back
            </Button>
            <Button
              size={'xl'}
              icon={ArrowRightIcon}
              iconProps={{ size: 21 }}
              iconPosition={'right'}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className={'signup-form-row'}>
          <div className={'signup-form-item signup-form-item--full'}>
            <Radio
              options={US_AUTH_OPTIONS}
              label={'Are you authorized for full-time employment in the US?'}
              name={'usAuth'}
              help={(
                <>
                  <strong>Why this question?</strong>{' '}
                  Most of our jobs are US-based and US-work authorization is required in many cases.
                  We also recruit internationally and with firms who are willing to sponsor candidates.
                </>
              )}
              checked={state?.workStatus?.usAuth}
              onChange={e => {
                setState(current => {
                  return {
                    ...current,
                    workStatus: {
                      ...current.workStatus,
                      usAuth: e.target.value,
                      usAuthExplain: '',
                      citizenship: e.target.value === 'permanent' ? null : current.workStatus.citizenship,
                    },
                  };
                });
              }}
              errors={{
                usAuth: formErrors.get('usAuth'),
                usAuthExplain: formErrors.get('usAuthExplain'),
              }}
              showCustom={true}
              customRadioValue={['other']}
              customPlaceholder={'Please explain'}
              customName={'usAuthExplain'}
              customOnChange={e => {
                setState(current => {
                  return {
                    ...current,
                    workStatus: {
                      ...current.workStatus,
                      usAuthExplain: e.target.value,
                    },
                  };
                });
              }}
              customValue={state?.workStatus?.usAuthExplain || ''}
            />
          </div>
          {state?.workStatus?.usAuth && state.workStatus.usAuth !== 'permanent' && (
            <div className={'signup-form-item signup-form-item--full'}>
              <AsyncSelectPane
                name={'primaryCity'}
                label={'What is your country of Citizenship?'}
                searchHelp={'If your country is not listed, begin typing to search for it.'}
                errors={formErrors.get('citizenship')}
                title={'Select your Citizenship'}
                help={(
                  <>
                    <strong>Why this question?</strong>{' '}
                    Occasionally, certain roles have country-specific or work authorization requirements. GoBuyside does not discriminate based on citizenship, and most positions are US-based and open to all applicants with valid work authorization
                  </>
                )}
                selected={state?.workStatus?.citizenship}
                columns={4}
                onChange={v => {
                  setState((current) => {
                    return {
                      ...current,
                      workStatus: {
                        ...current.workStatus,
                        citizenship: v,
                      },
                    };
                  });
                }}
                source={ENDPOINT.TAXONOMIES.CITIZENSHIP}
                notFoundText={'City is not found'}
                searchPlaceholder={'Enter the city name'}
                showIcon={false}
                autoselect
              />
            </div>
          )}
          <div className={'signup-form__subheader'}>
            <p className={'signup-form__subheader-title'}>
              <InputLabel
                label={'Please share demographic info (optional)'}
                help={(
                  <>
                    <strong>Why this question?</strong>{' '}
                    As part of our commitment to equal employment opportunity,
                    GoBuyside collects optional demographic information to support the
                    inclusion of underrepresented groups in the finance community.
                    In all cases, GoBuyside is dedicated to promoting equal opportunity
                    in hiring for all candidates, regardless of race, color, national
                    origin, sex, gender identity, sexual orientation, or creed.
                  </>
                )}>
              </InputLabel>
            </p>
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <Radio
              name={'discloseGender'}
              options={DISCLOSE_OPTIONS}
              label={'Which of these best describes your gender?'}
              checked={state?.workStatus?.discloseGender}
              onChange={e => {
                setState(current => {
                  return {
                    ...current,
                    workStatus: {
                      ...current.workStatus,
                      discloseGender: e.target.value,
                      gender: '',
                    },
                  };
                });
              }}
            />
            {state?.workStatus?.discloseGender === 'ok_to_say' && (
              <div className={'signup-form-item__sub-input'}>
                <Radio
                  name={'gender'}
                  errors={{
                    gender: formErrors.get('gender'),
                  }}
                  options={GENDER_OPTIONS}
                  checked={state?.workStatus?.gender}
                  onChange={e => {
                    setState(current => {
                      return {
                        ...current,
                        workStatus: {
                          ...current.workStatus,
                          gender: e.target.value,
                        },
                      };
                    });
                  }}
                />
              </div>
            )}
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <Radio
              label={
                'Which of the following best describes your racial identity?'
              }
              name={'discloseRace'}
              options={DISCLOSE_OPTIONS}
              checked={state?.workStatus?.discloseRace}
              onChange={e => {
                setState(current => {
                  return {
                    ...current,
                    workStatus: {
                      ...current.workStatus,
                      discloseRace: e.target.value,
                      race: [],
                      raceCustom: '',
                    },
                  };
                });
              }}
            />
            {state?.workStatus?.discloseRace === 'ok_to_say' && (
              <div className={'signup-form-item__sub-input'}>
                <Radio
                  name={'race'}
                  options={RACE_OPTIONS}
                  checked={state?.workStatus?.race || []}
                  errors={{
                    race: formErrors.get('race'),
                    raceCustom: formErrors.get('raceCustom'),
                  }}
                  onChange={e => {
                    setState(current => {
                      let race = [...(current?.workStatus?.race || [])];

                      if (!Array.isArray(race)) {
                        race = [];
                      }

                      if (
                        e.target.checked &&
                        race.indexOf(e.target.value) === -1
                      ) {
                        race.push(e.target.value);
                      } else if (
                        !e.target.checked &&
                        race.indexOf(e.target.value) !== -1
                      ) {
                        race = race.filter(r => r !== e.target.value);
                      }

                      return {
                        ...current,
                        workStatus: {
                          ...current.workStatus,
                          race,
                          raceCustom:
                            race.indexOf('other') !== -1
                              ? current.workStatus.raceCustom
                              : '',
                        },
                      };
                    });
                  }}
                  showCustom={true}
                  customRadioValue={'other'}
                  customPlaceholder={'Specify if desired'}
                  customName={'raceCustom'}
                  customOnChange={e => {
                    setState(current => {
                      return {
                        ...current,
                        workStatus: {
                          ...current.workStatus,
                          raceCustom: e.target.value,
                        },
                      };
                    });
                  }}
                  customValue={state?.workStatus?.raceCustom || ''}
                  multiple
                  checkedBold
                />
              </div>
            )}
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <Radio
              label={'Do you identify as LGBTQ+?'}
              name={'discloseLgbtq'}
              options={DISCLOSE_OPTIONS}
              checked={state?.workStatus?.discloseLgbtq}
              onChange={e => {
                setState(current => {
                  return {
                    ...current,
                    workStatus: {
                      ...current.workStatus,
                      discloseLgbtq: e.target.value,
                      lgbtq: '',
                    },
                  };
                });
              }}
            />
            {state?.workStatus?.discloseLgbtq === 'ok_to_say' && (
              <div className={'signup-form-item__sub-input'}>
                <Radio
                  name={'lgbtq'}
                  errors={{
                    lgbtq: formErrors.get('lgbtq'),
                  }}
                  options={LGBTQ_OPTIONS}
                  checked={state?.workStatus?.lgbtq}
                  onChange={e => {
                    setState(current => {
                      return {
                        ...current,
                        workStatus: {
                          ...current.workStatus,
                          lgbtq: e.target.value,
                        },
                      };
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </SignupLayout>
    </form>
  );
};

export default SignupStepSix;
